import { queryOptions } from '@tanstack/react-query';

import { authApi, type UserProfile } from '@/features/auth/api';
import { ApiError } from '@/lib/api';

/** The session, as the API sees it. One query the whole app shares. */
export const sessionQuery = queryOptions<UserProfile>({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    staleTime: 5 * 60 * 1000,
    retry: false,
});

export function isUnauthorized(error: unknown) {
    return error instanceof ApiError && error.status === 401;
}

/** 423: too many wrong passwords. The API's detail says how long and who can lift it. */
export function isLocked(error: unknown) {
    return error instanceof ApiError && error.status === 423;
}

/*
  Two small facts that survive a reload, so the login screen can pick the right state:

  - the last email that signed in here, kept in localStorage, decides between the first-time
    copy ("Use the email address your administrator set up for you") and the default copy,
    and pre-fills the field after an expiry
  - whether this tab had a session, kept in sessionStorage, is what makes a 401 read as
    "your session expired" rather than a first visit
*/
const LAST_EMAIL = 'alist-admin:last-email';
const HAD_SESSION = 'alist-admin:had-session';
// The address typed on the login screen, carried to the recovery screen so it need not be
// typed twice. Read once and gone: it is a hand-off, not a memory.
const RECOVERY_EMAIL = 'alist-admin:recovery-email';

function safeStorage(kind: 'local' | 'session'): Storage | undefined {
    try {
        return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        return undefined;
    }
}

export const sessionMemory = {
    lastEmail: () => safeStorage('local')?.getItem(LAST_EMAIL) ?? '',
    rememberEmail: (email: string) => safeStorage('local')?.setItem(LAST_EMAIL, email),
    hadSession: () => safeStorage('session')?.getItem(HAD_SESSION) === '1',
    markSession: () => safeStorage('session')?.setItem(HAD_SESSION, '1'),
    forgetSession: () => safeStorage('session')?.removeItem(HAD_SESSION),
    stashRecoveryEmail: (email: string) => {
        const storage = safeStorage('session');
        if (email) storage?.setItem(RECOVERY_EMAIL, email);
        else storage?.removeItem(RECOVERY_EMAIL);
    },
    takeRecoveryEmail: () => {
        const storage = safeStorage('session');
        const email = storage?.getItem(RECOVERY_EMAIL) ?? '';
        storage?.removeItem(RECOVERY_EMAIL);
        return email;
    },
};

/** Only same-origin paths may be returned to after login; anything else goes to the dashboard. */
export function safeRedirect(target: string | undefined, fallback = '/dashboard') {
    if (!target || !target.startsWith('/') || target.startsWith('//')) return fallback;
    return target;
}
